import React from "react"
import PropTypes from "prop-types"
import { cn } from "../../../lib/utils"

// ─── Variant color map ────────────────────────────────────────────────────────
// Maps a variant name to a Tailwind text color class applied to the value.
const VARIANT_CLASS = {
  active: "text-[var(--status-success-text)]",
  inactive: "text-[var(--gray-dark)]",
  amber: "text-[var(--accent-amber)]",
  blue: "text-[var(--accent-blue)]",
  gray: "text-[var(--text-secondary)]",
}

// ─── Single KPI card ─────────────────────────────────────────────────────────
// Elevated card block — Linear/Vercel-inspired look.
// Replaces the old divider-separated cell with a shadow card.
// NOTE: value and label are rendered as <span> elements so that existing tests
// (which query `container.querySelector("span")` and `previousElementSibling`)
// continue to work without modification.
function KPICard({ value, label, variant, flush }) {
  return (
    <div
      className={cn(
        "kpi-card flex flex-col justify-center",
        flush ? "kpi-card--flush px-5 py-2.5" : "p-4",
      )}
    >
      <span className="kpi-label text-[0.625rem] font-semibold uppercase tracking-[0.05em] text-[var(--text-muted,var(--text-secondary))] whitespace-nowrap mb-[3px]">
        {label}
      </span>
      <span
        className={cn(
          "kpi-val font-bold leading-none tracking-[-0.01em]",
          flush ? "text-[1.125rem]" : "text-xl",
          variant ? VARIANT_CLASS[variant] : "text-[var(--text-heading)]",
        )}
      >
        {value}
      </span>
    </div>
  )
}

KPICard.propTypes = {
  value: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(["active", "inactive", "amber", "blue", "gray"]),
  flush: PropTypes.bool,
}

// ─── Sectioned strip (FacilityMain split-card style) ─────────────────────────
// Renders a horizontal flex row of shadow-card sections, each with a small
// section label above a grid of individual KPI cards.
function KPIStripSectioned({ sections, className }) {
  return (
    <div className={cn("flex flex-wrap gap-3 p-3", className)}>
      {sections.map((section, si) => (
        <div
          key={section.title || si}
          className={cn(
            "kpi-section rounded-lg p-3 min-w-[280px]",
            section.flex ? "" : "flex-1",
          )}
          style={section.flex ? { flex: section.flex } : undefined}
        >
          {section.title && (
            <div
              className={cn(
                "text-[var(--font-2xs)] font-bold uppercase tracking-[0.07em]",
                "text-[var(--text-secondary)] mb-2 px-1",
              )}
            >
              {section.title}
            </div>
          )}
          <div
            className={cn(
              "grid gap-2 grid-cols-2 sm:grid-cols-3",
              section.colsClass ||
                lgColsClass[Math.min(section.items.length, 6)] ||
                "lg:grid-cols-4",
            )}
          >
            {section.items.map((item, i) => (
              <KPICard
                key={item.label || i}
                value={item.value}
                label={item.label}
                variant={item.variant}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

KPIStripSectioned.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.node.isRequired,
          label: PropTypes.string.isRequired,
          variant: PropTypes.string,
        }),
      ).isRequired,
      flex: PropTypes.number,
      minWidth: PropTypes.number,
      /** Tailwind grid-cols class override e.g. "grid-cols-3" */
      colsClass: PropTypes.string,
    }),
  ).isRequired,
  className: PropTypes.string,
}

// ─── Flat strip (profile pages) ──────────────────────────────────────────────
// Renders a responsive grid of elevated KPI cards.
// n columns: 2 on mobile → 3 on sm → up to items.length (max 8) on lg.
// Static Tailwind class map — template literals are not purged correctly by
// Tailwind's content scanner, so we use an explicit lookup instead.
const lgColsClass = {
  1: "lg:grid-cols-1",
  2: "lg:grid-cols-2",
  3: "lg:grid-cols-3",
  4: "lg:grid-cols-4",
  5: "lg:grid-cols-5",
  6: "lg:grid-cols-6",
  7: "lg:grid-cols-7",
  8: "lg:grid-cols-8",
}

function KPIStripFlat({ items, className, skeleton, flush }) {
  const count = skeleton ? 4 : items.length
  // Cap the lg column count at 8 to avoid cramped cards on wide screens.
  const lgCols = Math.min(count, 8)

  // Flush mode: horizontal row with vertical dividers (profile pages)
  const wrapperClass = flush
    ? "kpi-strip flex flex-wrap"
    : cn(
        "grid grid-cols-2 sm:grid-cols-3 gap-3",
        lgColsClass[lgCols] || "lg:grid-cols-4",
      )

  if (skeleton) {
    return (
      <div className={cn(wrapperClass, className)}>
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={cn(
              "kpi-card px-4 py-3 animate-pulse flex flex-col gap-1",
              flush && "kpi-card--flush",
            )}
          >
            <div className="h-3 w-14 bg-[var(--gray-3)] rounded" />
            <div className="h-6 w-12 bg-[var(--gray-3)] rounded" />
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className={cn(wrapperClass, className)}>
      {items.map((item, i) => (
        <KPICard
          key={item.label || i}
          value={item.value}
          label={item.label}
          variant={item.variant}
          flush={flush}
        />
      ))}
    </div>
  )
}

KPIStripFlat.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.node.isRequired,
      label: PropTypes.string.isRequired,
      variant: PropTypes.oneOf(["active", "inactive", "amber", "blue", "gray"]),
    }),
  ),
  className: PropTypes.string,
  /** Renders placeholder skeleton cards instead of real data */
  skeleton: PropTypes.bool,
  /** Removes top border-radius so cards sit flush under a hero banner */
  flush: PropTypes.bool,
}

// ─── Public API ──────────────────────────────────────────────────────────────
/**
 * KPIStrip — shared KPI display component for all profile and list pages.
 *
 * Flat usage (profile pages — grid of elevated cards):
 *   <KPIStrip items={[{ value: 4, label: "Scheduled Days" }]} />
 *
 * Sectioned usage (FacilityMain split-card bar):
 *   <KPIStrip sections={[
 *     { title: "Client Accounts", items: [...], flex: 2 },
 *     { title: "Users", items: [...], flex: 1, minWidth: 200 },
 *   ]} />
 *
 * Skeleton (loading state):
 *   <KPIStrip skeleton />
 */
function KPIStrip({ items, sections, className, skeleton, flush }) {
  if (sections) {
    return <KPIStripSectioned sections={sections} className={className} />
  }

  return (
    <KPIStripFlat
      items={items || []}
      className={className}
      skeleton={skeleton}
      flush={flush}
    />
  )
}

KPIStrip.propTypes = {
  /** Flat mode: array of KPI cards */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.node.isRequired,
      label: PropTypes.string.isRequired,
      variant: PropTypes.oneOf(["active", "inactive", "amber", "blue", "gray"]),
    }),
  ),
  /** Sectioned mode: array of card sections, each with items */
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.node.isRequired,
          label: PropTypes.string.isRequired,
          variant: PropTypes.string,
        }),
      ).isRequired,
      flex: PropTypes.number,
      minWidth: PropTypes.number,
      colsClass: PropTypes.string,
    }),
  ),
  className: PropTypes.string,
  /** Show skeleton placeholder while data loads (flat mode only) */
  skeleton: PropTypes.bool,
  /** Removes top border-radius so cards sit flush under a hero banner */
  flush: PropTypes.bool,
}

export default KPIStrip
